import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import {
  BaseForm,
  FormInput,
  FormSelect,
  FormToggle,
} from "components/BaseForm";
import { useCampaignActivityMutations } from "hooks/useCampaignActivityTable";
import CampaignService from "services/Campaign";
import ConstantContactAccountsService from "services/ConstantContactAccounts";
import ConstantContactCampaignsService from "services/ConstantContactCampaigns";
import EmailTemplateService from "services/EmailTemplate";

const requiredId = label =>
  z
    .union([z.number(), z.string()])
    .nullable()
    .refine(v => v != null && v !== "", `${label} is required`);

const schema = z.object({
  account_id: requiredId("Account"),
  cc_campaign_activity_id: z
    .string()
    .min(1, "Campaign Activity ID is required"),
  campaign_id: requiredId("Campaign"),
  template_id: requiredId("Email Template"),
  active: z.boolean(),
});

const CampaignActivityForm = ({ activityId, onSuccess, onCancel }) => {
  const { create, update } = useCampaignActivityMutations();

  const { data: accounts = [] } = useQuery({
    queryKey: ["cc-accounts-for-filters"],
    queryFn: () => ConstantContactAccountsService.getForFilters(),
  });
  const { data: campaigns = [] } = useQuery({
    queryKey: ["campaigns-for-filters"],
    queryFn: () => CampaignService.getCampaignsForFilters(),
  });
  const { data: templates = [] } = useQuery({
    queryKey: ["email-templates-for-filters"],
    queryFn: () => EmailTemplateService.getEmailTemplatesForFilters(),
  });

  const { data: activity } = useQuery({
    queryKey: ["cc-campaign-activity", activityId],
    queryFn: () =>
      ConstantContactCampaignsService.getCampaignActivity(activityId),
    enabled: !!activityId,
  });

  const accountOptions = accounts.map(a => ({
    key: a.id,
    value: a.id,
    text: a.name,
  }));
  const campaignOptions = campaigns.map(c => ({
    key: c.id,
    value: c.id,
    text: c.name,
  }));
  // FormSelect can't render disabled items, so drop templates that don't
  // support Constant Contact instead of greying them out.
  const templateOptions = templates
    .filter(
      t =>
        !(
          t.enabled &&
          t.info?.provider_list &&
          !t.info.provider_list.constant_contact
        )
    )
    .map(t => ({ key: t.id, value: t.id, text: t.name }));

  if (activityId && !activity) return null;

  const handleSubmit = async values => {
    if (activityId) {
      await update.mutateAsync({ id: activityId, data: values });
    } else {
      await create.mutateAsync(values);
    }
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={{
        account_id: activity?.account_id ?? null,
        cc_campaign_activity_id: activity?.cc_campaign_activity_id ?? "",
        campaign_id: activity?.campaign_id ?? null,
        template_id: activity?.template_id ?? null,
        active: activity ? !!activity.active : true,
      }}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      onCancel={onCancel}
      submitButtonText={activityId ? "Save" : "Create"}
    >
      <FormSelect
        name="account_id"
        label="Account"
        options={accountOptions}
        required
      />
      <FormInput
        name="cc_campaign_activity_id"
        label="Campaign Activity ID"
        required
      />
      <FormSelect
        name="campaign_id"
        label="Campaign"
        options={campaignOptions}
        required
      />
      <FormSelect
        name="template_id"
        label="Email Template"
        options={templateOptions}
        required
      />
      <FormToggle name="active" label="Active" />
    </BaseForm>
  );
};

export default CampaignActivityForm;
